import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevMode } from '../contexts/DevModeContext';
import Chart from 'react-apexcharts';
import ProjectModal from './ProjectModal';
import FlagIcon from './FlagIcon';
import Avatar from './Avatar';
import { getDeadlineColor, formatDate } from '../utils/deadlineUtils';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  FileText, 
  Calendar,
  Plus,
  Search,
  Check,
  X,
  Filter,
  PieChart as PieChartIcon,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  Edit2,
  Trash2,
  List,
  FolderKanban,
  Bell
} from 'lucide-react';

const AirportsAdminNew = () => {
  const navigate = useNavigate();
  const { devMode, dummyData } = useDevMode();
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingDeadline, setEditingDeadline] = useState(null);
  const [tempDeadline, setTempDeadline] = useState('');
  const [expandedUpdate, setExpandedUpdate] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [updateText, setUpdateText] = useState('');
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [weeklyHoursExpanded, setWeeklyHoursExpanded] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ title: '', items: [], type: '' });
  const [employeeViewMode, setEmployeeViewMode] = useState('list'); // 'list' or 'kanban'
  const [visibleStatuses, setVisibleStatuses] = useState(['Lead', 'Bid preparation', 'Submitted', 'Ongoing']); // Default visible statuses
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [userAvatars, setUserAvatars] = useState({});
  const [newUpdateIds, setNewUpdateIds] = useState(new Set());
  const previousUpdateIds = React.useRef(new Set());
  
  // State for API data
  const [apiProjects, setApiProjects] = useState([]);
  const [apiEmployees, setApiEmployees] = useState([]);
  const [apiMonthlyStats, setApiMonthlyStats] = useState([]);

  // Use dummy data in dev mode, API data otherwise
  const projects = devMode ? dummyData.allProjects : apiProjects;
  const employees = devMode ? dummyData.employees : apiEmployees;
  const monthlyStats = devMode ? dummyData.monthlyStats : apiMonthlyStats;
  
  // Get unique status values from actual projects for dropdown
  const uniqueStatuses = React.useMemo(() => {
    const statuses = [...new Set(
      projects
        .map(p => p.status)
        .filter(status => status != null && status !== '') // Filter out null/undefined/empty
        .map(status => String(status)) // Convert all to strings
        .filter(status => status.trim() !== '') // Remove empty strings after trim
    )];
    return statuses.sort();
  }, [projects]);
  
  // Charts now using ApexCharts - no debug needed

  useEffect(() => {
    if (devMode) {
      setLoading(false);
    } else {
      // Fetch real data from API
      fetchAirportsData();
      fetchStatusOptions();
      fetchRecentUpdates();
    }
  }, [devMode]);

  // Auto-refresh Recent Updates every 10 seconds
  useEffect(() => {
    if (devMode) return;
    
    const interval = setInterval(() => {
      fetchRecentUpdates();
    }, 10000); // 10 seconds
    
    return () => clearInterval(interval);
  }, [devMode]);

  // Listen for custom event when a project update is created
  useEffect(() => {
    const handleUpdateCreated = (event) => {
      console.log('🔔 Project update created event received:', event.detail);
      // Immediately refresh the Recent Updates list
      fetchRecentUpdates();
    };
    
    window.addEventListener('projectUpdateCreated', handleUpdateCreated);
    
    return () => {
      window.removeEventListener('projectUpdateCreated', handleUpdateCreated);
    };
  }, []);

  const fetchStatusOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/reference-data/project-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const statusList = data?.records || data || [];
      
      // Sort by order field if it exists
      const sorted = statusList.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.field_84_raw || '').localeCompare(b.field_84_raw || '');
      });
      
      setStatusOptions(sorted);
    } catch (error) {
      console.error('Failed to fetch status options:', error);
      setStatusOptions([]);
    }
  };

  const fetchRecentUpdates = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/projects/recent-updates?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const updates = data?.updates || [];
      
      // Check for new updates using ref
      if (previousUpdateIds.current.size > 0 && updates.length > 0) {
        const newIds = new Set();
        
        updates.forEach(update => {
          if (!previousUpdateIds.current.has(update.id)) {
            newIds.add(update.id);
            console.log('🆕 New update detected:', update.id, update.text?.substring(0, 50));
          }
        });
        
        if (newIds.size > 0) {
          console.log('🔔 Playing notification sound for', newIds.size, 'new updates');
          // Play notification sound
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.5;
          audio.play().catch(e => console.log('Could not play notification sound:', e));
          
          // Mark new updates
          setNewUpdateIds(newIds);
          
          // Remove highlight after 5 seconds
          setTimeout(() => {
            setNewUpdateIds(new Set());
          }, 5000);
        }
      }
      
      // Update ref with current update IDs
      previousUpdateIds.current = new Set(updates.map(u => u.id));
      
      setRecentUpdates(updates);
      
      // Fetch avatars for update authors
      const authorIds = updates
        .map(u => u.author_id)
        .filter(id => id); // Filter out null/undefined
      
      console.log('🔍 AirportsAdmin - Author IDs to fetch avatars:', authorIds);
      
      if (authorIds.length > 0) {
        const uniqueIds = [...new Set(authorIds)];
        console.log('🔍 AirportsAdmin - Unique author IDs:', uniqueIds);
        
        const avatarResponse = await fetch(`${BACKEND_URL}/api/users/avatars`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ user_ids: uniqueIds })
        });
        const avatarData = await avatarResponse.json();
        console.log('✅ AirportsAdmin - Received avatars:', avatarData?.avatars);
        setUserAvatars(avatarData?.avatars || {});
      } else {
        console.log('⚠️ AirportsAdmin - No author IDs found in updates');
      }
    } catch (error) {
      console.error('Failed to fetch recent updates:', error);
      setRecentUpdates([]);
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    // Dropped outside the list
    if (!destination) return;

    // Dropped in the same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newStatus = destination.droppableId;
    const projectId = draggableId;

    // Find the status ID from statusOptions
    const statusOption = statusOptions.find(s => (s.field_84_raw || s.field_84) === newStatus);
    if (!statusOption) return;

    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Update project status via API
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          field_85: statusOption.id,
          field_85_raw: [{ id: statusOption.id, identifier: newStatus }]
        })
      });

      if (response.ok) {
        // Refresh data
        fetchAirportsData();
      }
    } catch (error) {
      console.error('Failed to update project status:', error);
    }
  };


  const calculateMonthlyStats = (projects) => {
    const now = new Date();
    const stats = [];
    
    // Get last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStr = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      let newLeads = 0;
      let newProjects = 0;
      let projectsWon = 0;
      let projectsLost = 0;
      
      projects.forEach(p => {
        // Check if project was created in this month
        if (p.dateRegistered) {
          const regDate = new Date(p.dateRegistered);
          if (regDate >= monthStart && regDate <= monthEnd) {
            const status = (p.status || '').toLowerCase();
            if (status.includes('lead')) {
              newLeads++;
            } else {
              newProjects++;
            }
          }
        }
        
        // Check if project was won/lost in this month
        if (p.dateWon) {
          const wonDate = new Date(p.dateWon);
          if (wonDate >= monthStart && wonDate <= monthEnd) {
            projectsWon++;
          }
        }
        if (p.dateLost) {
          const lostDate = new Date(p.dateLost);
          if (lostDate >= monthStart && lostDate <= monthEnd) {
            projectsLost++;
          }
        }
      });
      
      stats.push({
        month: monthStr,
        newLeads,
        newProjects,
        projectsWon,
        projectsLost
      });
    }
    
    return stats;
  };

  const fetchAirportsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/admin/airports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch airports data');
      }

      const data = await response.json();
      console.log('=== AIRPORTS ADMIN API RESPONSE ===');
      console.log('Projects count:', data.projects?.length || 0);
      console.log('Employees count:', data.employees?.length || 0);
      
      // Sample first project to debug structure
      if (data.projects && data.projects.length > 0) {
        console.log('Sample Project (full):', data.projects[0]);
        console.log('Project status:', data.projects[0].status, 'Type:', typeof data.projects[0].status);
        console.log('Project country (field_117_raw):', data.projects[0].field_117_raw, 'Type:', typeof data.projects[0].field_117_raw);
        console.log('Project lastUpdate:', data.projects[0].lastUpdate);
        console.log('Project value (field_10_raw):', data.projects[0].field_10_raw);
      }
      
      // Transform API data to match component expectations
      const transformedProjects = (data.projects || []).map(p => {
        // Parse value - handle European and US currency formats
        let parsedValue = 0;
        if (p.field_10_raw) {
          if (typeof p.field_10_raw === 'number') {
            parsedValue = p.field_10_raw;
          } else if (typeof p.field_10_raw === 'string') {
            // Handle European format: €770.420 means 770,420 (dots as thousand separators)
            // Remove currency symbols first
            let cleaned = p.field_10_raw.replace(/[€$]/g, '').trim();
            
            // Check if it's European format (dots as thousand separator)
            // European: 770.420 or 1.234.567
            // US: 770,420 or 1,234,567.89
            if (cleaned.includes('.') && !cleaned.includes(',')) {
              // Likely European format - remove dots (thousand separators)
              cleaned = cleaned.replace(/\./g, '');
            } else if (cleaned.includes(',') && cleaned.includes('.')) {
              // Has both - determine which is decimal
              const lastDot = cleaned.lastIndexOf('.');
              const lastComma = cleaned.lastIndexOf(',');
              if (lastComma > lastDot) {
                // Comma is decimal: 1.234,56 (European)
                cleaned = cleaned.replace(/\./g, '').replace(',', '.');
              } else {
                // Dot is decimal: 1,234.56 (US)
                cleaned = cleaned.replace(/,/g, '');
              }
            } else {
              // Only commas - remove them (thousand separators)
              cleaned = cleaned.replace(/,/g, '');
            }
            
            parsedValue = parseFloat(cleaned) || 0;
          }
        }

        // Parse probability
        let parsedProbability = 0;
        if (p.field_11_raw || p.probability) {
          const probValue = p.field_11_raw || p.probability;
          if (typeof probValue === 'number') {
            parsedProbability = probValue;
          } else if (typeof probValue === 'string') {
            parsedProbability = parseFloat(probValue.replace(/[%]/g, '')) || 0;
          }
        }

        // Build team array from owner and assistants
        // Normalize whitespace to handle inconsistent spacing in names
        const normalizeWhitespace = (str) => str ? str.replace(/\s+/g, ' ').trim() : '';
        
        const team = [];
        if (p.field_237_raw && p.field_237_raw.length > 0) {
          const ownerName = normalizeWhitespace(p.field_237_raw[0].identifier);
          if (ownerName) team.push(ownerName);
        }
        if (p.field_57_raw && Array.isArray(p.field_57_raw)) {
          p.field_57_raw.forEach(assistant => {
            const assistantName = normalizeWhitespace(assistant.identifier);
            if (assistantName && !team.includes(assistantName)) {
              team.push(assistantName);
            }
          });
        }

        return {
          ...p,
          value: parsedValue,
          probability: parsedProbability,
          deadline: p.field_9_raw || '',
          status: p.status || 'Unknown',
          progress: 50,
          health: 'on-track',
          updateHistory: p.updateHistory || [],
          lastUpdate: p.lastUpdate || null,
          team: team
        };
      });
      
      const transformedEmployees = (data.employees || []).map(emp => ({
        id: emp.id,
        name: emp.name ? emp.name.replace(/\s+/g, ' ').trim() : '',
        email: emp.email,
        role: emp.role,
        avatar_url: emp.avatar_url,
        activeProjects: emp.activeProjects || 0,
        hoursThisWeek: emp.hoursThisWeek || 0,
        utilization: emp.utilization || 0,
        yearlyHours: emp.yearlyHours || {},
        weeklyHours: (emp.weeklyHours || []).map(w => ({
          week: w.week,
          hours: w.hours || 0,
          projects: (w.projects || []).map(p => ({
            name: p.name || 'Unknown',
            hours: p.hours || 0
          }))
        })),
        unsolvedIncidents: emp.unsolvedIncidents || 0,
        unsolvedIncidentIds: emp.unsolvedIncidentIds || [],
        projectsNotUpdated: emp.projectsNotUpdated || 0,
        projectsNotUpdatedIds: emp.projectsNotUpdatedIds || [],
        projectsUpdated: emp.projectsUpdated || 0,
        projectsUpdatedIds: emp.projectsUpdatedIds || []
      }));
      
      console.log('✅ Transformed projects:', transformedProjects.length);
      console.log('✅ Transformed employees:', transformedEmployees.length);
      console.log('✅ Monthly stats:', data.monthly_stats?.length || 0);
      
      // Calculate monthly stats from projects if not provided
      let monthlyStatsData = data.monthly_stats || [];
      if (monthlyStatsData.length === 0 && transformedProjects.length > 0) {
        monthlyStatsData = calculateMonthlyStats(transformedProjects);
      }
      
      // Set transformed data
      setApiProjects(transformedProjects);
      setApiEmployees(transformedEmployees);
      setApiMonthlyStats(monthlyStatsData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching airports data:', error);
      setLoading(false);
    }
  };

  // Calculate metrics
  const calculateMetrics = () => {
    // Add safety checks and case-insensitive matching
    const ongoingProjects = projects.filter(p => {
      if (!p || !p.status) return false;
      const status = String(p.status).toLowerCase();
      return status.includes('ongoing') || status.includes('active');
    });
    
    const bidProjects = projects.filter(p => {
      if (!p || !p.status) return false;
      const status = String(p.status).toLowerCase();
      return status.includes('bid');
    });
    
    const leadProjects = projects.filter(p => {
      if (!p || !p.status) return false;
      const status = String(p.status).toLowerCase();
      return status.includes('lead');
    });
    
    const submittedProjects = projects.filter(p => {
      if (!p) return false;
      const stage = String(p.stage || '').toLowerCase();
      const status = String(p.status || '').toLowerCase();
      return stage.includes('submitted') || status.includes('submitted');
    });
    
    const totalOpenValue = ongoingProjects.reduce((sum, p) => sum + (p.value || 0), 0);
    const totalProposalValue = [...bidProjects, ...leadProjects].reduce((sum, p) => sum + (p.value || 0), 0);
    
    // Projects waiting for final payment
    const projectsWaitingPayment = projects.filter(p => p && p.waitingFinalPayment);
    const totalWaitingPayment = projectsWaitingPayment.reduce((sum, p) => sum + (p.value || 0), 0);

    return {
      totalOpenValue,
      totalProposalValue,
      leadCount: leadProjects.length,
      bidPrepCount: bidProjects.length,
      submittedCount: submittedProjects.length,
      ongoingCount: ongoingProjects.length,
      totalWaitingPayment
    };
  };

  const metrics = calculateMetrics();

  // Format currency in Euros
  const formatCurrency = (value) => {
    // Handle invalid values
    let numValue = 0;
    
    if (typeof value === 'number' && !isNaN(value)) {
      numValue = value;
    } else if (typeof value === 'string') {
      numValue = parseFloat(value) || 0;
    } else if (value !== null && value !== undefined) {
      numValue = parseFloat(value) || 0;
    }
    
    try {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numValue);
    } catch (error) {
      console.error('formatCurrency error:', error, 'value:', value);
      return `€${numValue.toLocaleString('de-DE')}`;
    }
  };

  // Get employee details
  const getEmployeeDetails = (employeeId) => {
    return employees.find(e => e.id === employeeId);
  };

  // Get employee's projects (only where they are the manager/owner)
  const getEmployeeProjects = (employeeName) => {
    if (!employeeName) return [];
    // Normalize whitespace for consistent comparison
    const normalizedName = employeeName.replace(/\s+/g, ' ').trim();
    return projects.filter(p => {
      if (!p || !p.field_237_raw) return false;
      // Check if employee is the owner (manager)
      const owner = p.field_237_raw[0];
      if (!owner) return false;
      const ownerName = owner.identifier ? owner.identifier.replace(/\s+/g, ' ').trim() : '';
      return ownerName === normalizedName;
    });
  };

  // Calculate employee stats
  const calculateEmployeeStats = (employee) => {
    const employeeProjects = getEmployeeProjects(employee.name);
    
    // Count ongoing and submitted projects
    const ongoingProjects = employeeProjects.filter(p => {
      if (!p || !p.status) return false;
      const status = String(p.status).toLowerCase();
      return status.includes('ongoing') || status.includes('active');
    }).length;
    
    const submittedProjects = employeeProjects.filter(p => {
      if (!p || !p.status) return false;
      const status = String(p.status).toLowerCase();
      return status.includes('submitted');
    }).length;
    
    // Calculate total project value
    const projectValue = employeeProjects.reduce((sum, p) => {
      return sum + (p.value || 0);
    }, 0);
    
    // Get data from backend
    const unsolvedIncidents = employee.unsolvedIncidents || 0;
    const projectsNotUpdated = employee.projectsNotUpdated || 0;
    const projectsUpdated = employee.projectsUpdated || 0;
    const hoursThisWeek = employee.hoursThisWeek || 0;
    
    // Get weekly hours data
    const weeklyHours = employee.weeklyHours || [];
    const hoursLastWeek = weeklyHours.length >= 2 ? weeklyHours[weeklyHours.length - 2].hours : 0;
    const hoursLastMonth = weeklyHours.reduce((sum, w) => sum + (w.hours || 0), 0);
    
    return {
      ongoingProjects,
      submittedProjects,
      projectValue,
      unsolvedIncidents,
      projectsNotUpdated,
      projectsUpdated,
      hoursThisWeek,
      hoursLastWeek,
      hoursLastMonth
    };
  };
  
  // Show modal with project/incident details
  const showDetailsModal = (title, ids, type) => {
    let items = [];
    
    if (type === 'projects') {
      items = projects.filter(p => ids.includes(p.id)).map(p => ({
        id: p.id,
        name: p.name || 'Unnamed Project',
        status: p.status || 'No status'
      }));
    } else if (type === 'incidents') {
      // Would need to fetch incidents, for now show IDs
      items = ids.map(id => ({ id, name: `Incident ${id.slice(0, 8)}` }));
    }
    
    setModalData({ title, items, type });
    setShowModal(true);
  };

  // Filter projects by status and visible statuses
  const filterProjectsByStatus = (projectList) => {
    let filtered = projectList;
    
    // Filter by visible statuses
    if (visibleStatuses.length > 0) {
      filtered = filtered.filter(p => {
        if (!p || !p.status) return false;
        return visibleStatuses.includes(p.status);
      });
    }
    
    // Additional specific status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => {
        if (!p || !p.status) return false;
        const projectStatus = String(p.status).toLowerCase();
        const filterStatus = String(statusFilter).toLowerCase();
        return projectStatus === filterStatus;
      });
    }
    
    return filtered;
  };

  // Calculate project value with probability
  const calculateProjectValue = (project) => {
    if (!project) return 0;
    const value = project.value || 0;
    const probability = project.probability || 0;
    return (value * probability) / 100;
  };

  // Handle deadline edit
  const startEditingDeadline = (projectId, currentDeadline) => {
    setEditingDeadline(projectId);
    setTempDeadline(currentDeadline);
  };

  const saveDeadline = async (projectId) => {
    // TODO: Save to API
    console.log(`Saving deadline for project ${projectId}: ${tempDeadline}`);
    setEditingDeadline(null);
    // In real implementation, would update the project deadline via API
  };

  const cancelEditingDeadline = () => {
    setEditingDeadline(null);
    setTempDeadline('');
  };

  // Handle update modal
  const openUpdateModal = (project) => {
    setSelectedProject(project);
    setUpdateText('');
    setShowUpdateModal(true);
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedProject(null);
    setUpdateText('');
  };

  const saveUpdate = async () => {
    if (!updateText.trim() || !selectedProject) return;

    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Create update using the proper updates endpoint
      const response = await fetch(`${BACKEND_URL}/api/projects/${selectedProject.id}/updates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: updateText,
          date: new Date().toISOString()
        })
      });

      if (response.ok) {
        console.log('✅ Update created successfully');
        
        // Dispatch event to notify other components (including Recent Updates list)
        window.dispatchEvent(new CustomEvent('projectUpdateCreated', {
          detail: { 
            projectId: selectedProject.id, 
            text: updateText,
            type: 'manual_update'
          }
        }));
        
        // Close modal and clear text
        closeUpdateModal();
        
        // Show success message (brief, non-blocking)
        console.log('🎉 Update saved and will appear in Recent Updates');
      } else {
        const errorData = await response.json();
        console.error('Failed to save update:', errorData);
        alert(`Failed to save update: ${errorData.detail || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error saving update:', error);
      alert('Error saving update. Please check your connection and try again.');
    }
  };

  // Chart colors
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }


  const openEditProjectModal = (project) => {
    setEditingProject(project);
    setShowEditProjectModal(true);
  };

  const closeEditProjectModal = () => {
    setShowEditProjectModal(false);
    setEditingProject(null);
  };

  const handleProjectSaved = () => {
    closeEditProjectModal();
    fetchAirportsData();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Airports Division Admin</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Comprehensive project and staff monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          {devMode && (
            <div className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-sm font-medium">
              Dev Mode Active
            </div>
          )}
          <button
            onClick={() => alert('Project registration modal would open here')}

            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Register Project
          </button>
        </div>
      </div>

      {/* Financial & Status Metrics - Combined */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Financial Values Box - 25% (1 of 4 columns) */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Financial Overview
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">Open Projects</span>
                <span className="text-xs text-gray-500 dark:text-gray-500">{metrics.ongoingCount} projects</span>
              </div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(metrics.totalOpenValue)}
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">Proposal Pipeline</span>
                <span className="text-xs text-gray-500 dark:text-gray-500">{metrics.leadCount + metrics.bidPrepCount} proposals</span>
              </div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {formatCurrency(metrics.totalProposalValue)}
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">Awaiting Final Payment</span>
              </div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formatCurrency(metrics.totalWaitingPayment)}
              </div>
            </div>
          </div>
        </div>

        {/* Project Status Counts - 25% (1 of 4 columns) */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Project Pipeline Status
          </h3>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/projects?status=lead')}
              className="w-full flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Leads</span>
              <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{metrics.leadCount}</span>
            </button>
            <button
              onClick={() => navigate('/projects?status=bid')}
              className="w-full flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Bid Preparation</span>
              <span className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{metrics.bidPrepCount}</span>
            </button>
            <button
              onClick={() => navigate('/projects?status=submitted')}
              className="w-full flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Submitted</span>
              <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">{metrics.submittedCount}</span>
            </button>
            <button
              onClick={() => navigate('/projects?status=ongoing')}
              className="w-full flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ongoing</span>
              <span className="text-2xl font-bold text-green-600 dark:text-green-400">{metrics.ongoingCount}</span>
            </button>
          </div>
        </div>

        {/* Recent Updates Box - 50% (2 of 4 columns) */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            Recent Updates
          </h3>
          <div className="max-h-[300px] overflow-y-auto space-y-3">
            {recentUpdates.length > 0 ? (
              recentUpdates.map((update, index) => {
                const isNew = newUpdateIds.has(update.id);
                return (
                <div 
                  key={index} 
                  className={`p-3 rounded border transition-all duration-500 ${
                    isNew 
                      ? 'bg-red-100 dark:bg-red-900/50 border-red-400 dark:border-red-500 shadow-lg' 
                      : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {update.author_id ? (
                      <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
                        <Avatar 
                          userId={update.author_id} 
                          userName={update.author}
                          avatarUrl={userAvatars[update.author_id]}
                          size="sm" 
                          className="w-full h-full" 
                        />
                      </div>
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 text-[8px] font-medium flex-shrink-0">
                        {update.author ? update.author.charAt(0).toUpperCase() : '?'}
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 flex-1 min-w-0">
                      <button
                        onClick={() => {
                          const project = projects.find(p => p.id === update.project_id);
                          if (project) {
                            openEditProjectModal(project);
                          }
                        }}
                        className="font-semibold text-sm text-blue-600 dark:text-blue-400 hover:underline truncate text-left"
                      >
                        {update.project_ge} - {update.project_name}
                      </button>
                      <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {update.date} {update.time}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2">
                    {update.text}
                  </div>
                </div>
                );
              })
            ) : (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                <p className="text-sm">No recent updates</p>
              </div>
            )}
          </div>
        </div>

        {/* Monthly Activity Graph - Hidden for now */}
        {false && (
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Monthly Activity (Last 12 Months)
            </h3>
          </div>
        )}
      </div>

      {/* Employee Cards Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
          <Users className="w-6 h-6 text-blue-600" />
          Airport Division Team
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {employees.filter(emp => emp).map(emp => {
            const stats = calculateEmployeeStats(emp);
            const initials = emp.name ? emp.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??';
            
            return (
              <button
                key={emp.id}
                onClick={() => setSelectedEmployee(emp.id)}
                className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-lg transition-all duration-200 text-left bg-white dark:bg-gray-800"
              >
                {/* Avatar - Clickable to select employee */}
                <div className="flex items-center gap-3 mb-4 cursor-pointer" onClick={() => setSelectedEmployee(emp.id)}>
                  {emp.avatar_url ? (
                    <img 
                      src={emp.avatar_url} 
                      alt={emp.name}
                      className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg"
                    style={{display: emp.avatar_url ? 'none' : 'flex'}}
                  >
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">{emp.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{emp.email || 'No email'}</p>
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 dark:text-gray-400">Ongoing Projects:</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">{stats.ongoingProjects}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 dark:text-gray-400">Submitted:</span>
                    <span className="font-semibold text-purple-600 dark:text-purple-400">{stats.submittedProjects}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600 dark:text-gray-400">Projects Value:</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(stats.projectValue)}</span>
                  </div>
                  <div 
                    className="flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded"
                    onClick={() => (emp.projectsNotUpdatedIds && emp.projectsNotUpdatedIds.length > 0) && showDetailsModal(`${emp.name} - Projects Update Status`, [...emp.projectsNotUpdatedIds, ...emp.projectsUpdatedIds], 'projects')}
                  >
                    <span className="text-xs text-gray-600 dark:text-gray-400">Updated (2w):</span>
                    <span className="font-semibold">
                      <span className="text-red-600 dark:text-red-400">{stats.projectsNotUpdated}</span>
                      <span className="text-gray-500 dark:text-gray-400">/</span>
                      <span className="text-green-600 dark:text-green-400">{stats.projectsUpdated}</span>
                    </span>
                  </div>
                  <div 
                    className="flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded"
                    onClick={() => emp.unsolvedIncidentIds && emp.unsolvedIncidentIds.length > 0 && showDetailsModal(`${emp.name} - Open Incidents`, emp.unsolvedIncidentIds, 'incidents')}
                  >
                    <span className="text-xs text-gray-600 dark:text-gray-400">Open Incidents:</span>
                    <span className="font-semibold text-red-600 dark:text-red-400">{stats.unsolvedIncidents}</span>
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Hours Worked:</div>
                    <div className="grid grid-cols-3 gap-1 text-xs">
                      <div>
                        <div className="text-gray-500 dark:text-gray-500">Week</div>
                        <div className="font-semibold text-gray-900 dark:text-white">{stats.hoursThisWeek}h</div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-500">Last</div>
                        <div className="font-semibold text-gray-900 dark:text-white">{stats.hoursLastWeek}h</div>
                      </div>
                      <div>
                        <div className="text-gray-500 dark:text-gray-500">Month</div>
                        <div className="font-semibold text-gray-900 dark:text-white">{stats.hoursLastMonth}h</div>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        
        {employees.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No employees found for this division</p>
          </div>
        )}
      </div>

      {/* Employee Analysis Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Employee Details</h2>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={selectedEmployee || ''}
                onChange={(e) => setSelectedEmployee(e.target.value || null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Employee...</option>
                {[...employees].sort((a, b) => a.name.localeCompare(b.name)).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {selectedEmployee && (() => {
          const employee = getEmployeeDetails(selectedEmployee);
          if (!employee) return null;

          const employeeProjects = getEmployeeProjects(employee.name);
          const filteredProjects = filterProjectsByStatus(employeeProjects);

          // Get unique statuses from THIS employee's projects
          const employeeStatuses = [...new Set(
            employeeProjects
              .map(p => p.status)
              .filter(status => status != null && status !== '')
              .map(status => String(status))
              .filter(status => status.trim() !== '')
          )].sort();

          // Prepare pie chart data for weekly hours - FILTER OUT UNDEFINED/NULL NAMES
          const pieData = (employee.weeklyHours[employee.weeklyHours.length - 1]?.projects || [])
            .filter(p => p && p.name && p.hours > 0) // Remove undefined/null names and zero hours
            .map(p => ({
              name: String(p.name), // Ensure string type
              value: p.hours
            }));

          return (
            <div className="p-6">
              {/* Employee Header - 40/60 Split Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                {/* Left: Employee Info - 40% (2 of 5 columns) */}
                <div className="lg:col-span-2 flex items-start gap-4">
                  {employee.avatar_url ? (
                    <img 
                      src={employee.avatar_url} 
                      alt={employee.name}
                      className="w-24 h-24 flex-shrink-0 rounded-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-24 h-24 flex-shrink-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center"
                    style={{display: employee.avatar_url ? 'none' : 'flex'}}
                  >
                    <span className="text-white font-bold text-3xl">
                      {employee.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{employee.name}</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-1">{employee.role}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">{employee.email}</p>
                  </div>
                </div>
                
                {/* Right: Yearly Hours Chart - HIDDEN as per requirement */}
                <div className="lg:col-span-3">
                  {/* Yearly hours hidden until data is available */}
                </div>
              </div>

              {/* Weekly Hours - Pie Chart + Stacked Bars - COLLAPSIBLE */}
              {employee.weeklyHours && employee.weeklyHours.length > 0 && employee.weeklyHours.some(w => w.hours > 0) ? (
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setWeeklyHoursExpanded(!weeklyHoursExpanded)}
                  className="w-full text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-blue-600" />
                    Weekly Hours Breakdown (Last 8 Weeks)
                  </div>
                  <ChevronDown className={`w-5 h-5 transition-transform ${weeklyHoursExpanded ? 'rotate-180' : ''}`} />
                </button>
                {weeklyHoursExpanded && (
                <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
                  {/* Pie Chart - 30% width (3 columns) */}
                  <div className="lg:col-span-3">
                    <Chart
                      options={{
                        chart: { type: 'pie', height: 300 },
                        labels: pieData.map(d => String(d.name || 'Unknown')),
                        colors: COLORS,
                        legend: { position: 'bottom', fontSize: '11px' },
                        dataLabels: { enabled: true, style: { fontSize: '11px' } },
                      }}
                      series={pieData.map(d => Number(d.value) || 0)}
                      type="pie"
                      height={300}
                    />
                  </div>

                  {/* Stacked Bars - 70% width (7 columns) - HORIZONTAL */}
                  <div className="lg:col-span-7">
                    <Chart
                      options={{
                        chart: { 
                          type: 'bar', 
                          height: 300, 
                          stacked: true,
                          toolbar: { show: false }
                        },
                        plotOptions: { 
                          bar: { 
                            horizontal: true,
                            dataLabels: { 
                              total: { 
                                enabled: true, 
                                style: { fontSize: '11px', fontWeight: 600 },
                                formatter: function (val) {
                                  return val.toFixed(0) + 'h'
                                }
                              }
                            }
                          } 
                        },
                        xaxis: { 
                          labels: { 
                            style: { fontSize: '11px' },
                            formatter: function(val) {
                              return val + 'h'
                            }
                          }
                        },
                        yaxis: { 
                          categories: (employee.weeklyHours || []).map(w => String(w?.week || 'Week')),
                          labels: { style: { fontSize: '11px' } }
                        },
                        colors: COLORS,
                        legend: { 
                          position: 'top',
                          fontSize: '11px',
                          markers: { width: 12, height: 12 }
                        },
                        dataLabels: { 
                          enabled: true,
                          style: { fontSize: '10px', colors: ['#fff'] },
                          formatter: function(val, opts) {
                            return val > 5 ? val + 'h' : ''
                          }
                        },
                        tooltip: {
                          y: {
                            formatter: (val) => `${val}h`,
                            title: {
                              formatter: (seriesName) => seriesName + ':'
                            }
                          }
                        }
                      }}
                      series={(() => {
                        // Get unique project names - FILTER OUT UNDEFINED/NULL
                        const projectNames = [...new Set(
                          (employee.weeklyHours || []).flatMap(week => 
                            (week?.projects || [])
                              .filter(p => p && p.name) // Remove undefined/null
                              .map(p => String(p.name)) // Ensure string type
                          )
                        )].filter(name => name && String(name).trim() !== ''); // Remove empty strings
                        
                        // Create series data for each project
                        return projectNames.map(projectName => ({
                          name: String(projectName), // Ensure string
                          data: (employee.weeklyHours || []).map(week => {
                            const project = (week?.projects || []).find(p => p && p.name === projectName);
                            return Number(project?.hours) || 0; // Ensure number
                          })
                        }));
                      })()}
                      type="bar"
                      height={300}
                    />
                  </div>
                </div>
                )}
              </div>
              ) : (
                <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setWeeklyHoursExpanded(!weeklyHoursExpanded)}
                    className="w-full text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <PieChartIcon className="w-5 h-5 text-blue-600" />
                      Weekly Hours Breakdown
                    </div>
                    <ChevronDown className={`w-5 h-5 transition-transform ${weeklyHoursExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  {weeklyHoursExpanded && (
                    <div className="h-[200px] flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
                      <p>No weekly hours data available</p>
                    </div>
                  )}
                </div>
              )}

              {/* Employee's Projects */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Current Projects ({filteredProjects.length})
                  </h4>
                  <div className="flex items-center gap-3">
                    {/* Status Visibility Filter */}
                    <div className="relative">
                      <button
                        onClick={() => setShowModal({ type: 'statusFilter' })}
                        className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        <Filter className="w-4 h-4" />
                        Visible ({visibleStatuses.length})
                      </button>
                    </div>
                    
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
                    >
                      <option value="all">All Statuses</option>
                      {employeeStatuses.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                    {/* View Mode Toggle */}
                    <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                      <button
                        onClick={() => setEmployeeViewMode('list')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                          employeeViewMode === 'list' 
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' 
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                        title="List View"
                      >
                        <List className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEmployeeViewMode('kanban')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                          employeeViewMode === 'kanban' 
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' 
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                        title="Kanban View"
                      >
                        <FolderKanban className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Group projects by status - FOLLOWING PROJECT SETTINGS ORDER */}
                {(() => {
                  // Group filtered projects by status
                  const groupedProjects = filteredProjects.reduce((groups, project) => {
                    const status = project.status || 'Unknown';
                    if (!groups[status]) groups[status] = [];
                    groups[status].push(project);
                    return groups;
                  }, {});

                  // Get ordered statuses from settings
                  const orderedStatuses = statusOptions.length > 0 
                    ? statusOptions.map(s => s.field_84_raw).filter(s => groupedProjects[s])
                    : Object.keys(groupedProjects);
                  
                  // Add any statuses not in settings (like 'Unknown')
                  Object.keys(groupedProjects).forEach(status => {
                    if (!orderedStatuses.includes(status)) {
                      orderedStatuses.push(status);
                    }
                  });

                  // KANBAN VIEW
                  if (employeeViewMode === 'kanban') {
                    return (
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <div className="overflow-x-auto pb-4 relative z-0">
                          <div className="flex gap-4 min-w-min">
                            {orderedStatuses.map(status => {
                              const statusProjects = groupedProjects[status];
                              return (
                                <div key={status} className="flex-shrink-0 w-80 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 rounded-t-lg">
                                    <div className="flex items-center justify-between">
                                      <h5 className="font-semibold text-gray-900 dark:text-white">{status}</h5>
                                      <span className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-600 px-2 py-0.5 rounded-full">
                                        {statusProjects.length}
                                      </span>
                                    </div>
                                  </div>
                                  <Droppable droppableId={status}>
                                    {(provided, snapshot) => (
                                      <div 
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className={`p-3 space-y-3 max-h-[800px] overflow-y-auto ${snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                                      >
                                        {statusProjects.map((project, index) => {
                                          // Calculate deadline color
                                          const getDeadlineColor = (deadlineStr) => {
                                            if (!deadlineStr || deadlineStr === '-' || deadlineStr === 'No deadline') return 'text-gray-600 dark:text-gray-400';
                                            try {
                                              // Parse deadline (format: DD/MM/YYYY or MM/DD/YYYY)
                                              let deadlineDate;
                                              if (deadlineStr.includes('/')) {
                                                const parts = deadlineStr.split('/');
                                                if (parts.length === 3) {
                                                  // Try DD/MM/YYYY format first
                                                  deadlineDate = new Date(parts[2], parts[1] - 1, parts[0]);
                                                  // If invalid, try MM/DD/YYYY
                                                  if (isNaN(deadlineDate.getTime())) {
                                                    deadlineDate = new Date(parts[2], parts[0] - 1, parts[1]);
                                                  }
                                                }
                                              }
                                              
                                              if (!deadlineDate || isNaN(deadlineDate.getTime())) {
                                                return 'text-gray-600 dark:text-gray-400';
                                              }
                                              
                                              const now = new Date();
                                              now.setHours(0, 0, 0, 0);
                                              deadlineDate.setHours(0, 0, 0, 0);
                                              
                                              const diffDays = Math.floor((deadlineDate - now) / (1000 * 60 * 60 * 24));
                                              
                                              if (diffDays < 0) return 'text-red-600 dark:text-red-400'; // Passed
                                              if (diffDays <= 7) return 'text-orange-600 dark:text-orange-400'; // Within a week
                                              return 'text-green-600 dark:text-green-400'; // More than a week
                                            } catch {
                                              return 'text-gray-600 dark:text-gray-400';
                                            }
                                          };
                                          
                                          const deadlineColor = getDeadlineColor(project.deadline);
                                          
                                          return (
                                            <Draggable key={project.id} draggableId={project.id} index={index}>
                                              {(provided, snapshot) => (
                                                <div
                                                  ref={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  {...provided.dragHandleProps}
                                                  className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:shadow-md transition ${snapshot.isDragging ? 'opacity-50 shadow-lg' : ''}`}
                                                >
                                                  <div className="flex items-center justify-between gap-2 mb-2">
                                                    <div className="flex items-center gap-2 flex-1">
                                                      <FlagIcon countryCode={project.field_117_raw} />
                                                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                                        {project.field_2_raw}
                                                      </span>
                                                    </div>
                                                    {/* Edit button */}
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditProjectModal(project);
                                                      }}
                                                      className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                                      title="Edit Project & View Update History"
                                                    >
                                                      <Edit2 className="w-3.5 h-3.5" />
                                                    </button>
                                                  </div>
                                                  <h6 className="text-sm font-bold text-gray-900 dark:text-white line-clamp-2 mb-2">
                                        {project.field_5_raw}
                                      </h6>
                                      
                                      {/* Last Update - Clickable to open edit modal with update history */}
                                      {project.lastUpdate && (() => {
                                        const getUpdateAgeColor = (dateStr) => {
                                          if (!dateStr) return 'text-gray-500';
                                          try {
                                            const [day, month, year] = dateStr.split('/');
                                            const updateDate = new Date(year, month - 1, day);
                                            const now = new Date();
                                            const diffDays = Math.floor((now - updateDate) / (1000 * 60 * 60 * 24));
                                            
                                            if (diffDays < 7) return 'text-green-600 dark:text-green-400';
                                            if (diffDays < 14) return 'text-gray-600 dark:text-gray-400';
                                            return 'text-red-600 dark:text-red-400';
                                          } catch {
                                            return 'text-gray-600 dark:text-gray-400';
                                          }
                                        };
                                        
                                        const ageColor = getUpdateAgeColor(project.lastUpdate.date);
                                        
                                        return (
                                          <div 
                                            className="mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openEditProjectModal(project);
                                            }}
                                            title="Click to view/edit update history"
                                          >
                                            <div className="flex items-center gap-1 mb-1">
                                              <Clock className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                              <span className={`text-[10px] font-medium ${ageColor}`}>
                                                {project.lastUpdate.date}
                                              </span>
                                            </div>
                                            <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2">
                                              {project.lastUpdate.text || 'No recent updates'}
                                            </p>
                                          </div>
                                        );
                                      })()}
                                      
                                      <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                                        <div className="flex justify-between">
                                          <span>Deadline:</span>
                                          <span className={`font-medium ${deadlineColor}`}>{formatDate(project.deadline)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Value:</span>
                                          <span className="font-bold text-green-600 dark:text-green-400">
                                            {formatCurrency(calculateProjectValue(project))}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                              )}
                                            </Draggable>
                                          );
                                        })}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </DragDropContext>
                    );
                  }

                  // LIST VIEW (existing)
                  return orderedStatuses.map(status => {
                    const statusProjects = groupedProjects[status];
                    return (
                    <div key={status} className="mb-6">
                      <h5 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 pb-2 border-b border-gray-300 dark:border-gray-600">
                        {status} ({statusProjects.length})
                      </h5>
                      <div className="space-y-3">
                        {statusProjects.map(project => (
                    <div key={project.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              {/* Country Flag */}
                              <FlagIcon countryCode={project.field_117_raw} />
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {project.field_2_raw}
                              </span>
                              <span className="text-sm font-bold text-gray-900 dark:text-white">
                                {project.field_5_raw}
                              </span>
                              {/* Status Dropdown */}
                              <select
                                value={project.status || ''}
                                onChange={(e) => {
                                  const statusOption = statusOptions.find(s => (s.field_84_raw || s.field_84) === e.target.value);
                                  if (statusOption) {
                                    // Call same handler as drag-and-drop
                                    handleDragEnd({
                                      source: { droppableId: project.status },
                                      destination: { droppableId: e.target.value },
                                      draggableId: project.id
                                    });
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="ml-auto px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                {statusOptions.map(status => (
                                  <option key={status.id} value={status.field_84_raw || status.field_84}>
                                    {status.field_84_raw || status.field_84}
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Last Update - Clickable */}
                            {project.lastUpdate && (() => {
                              // Calculate update age
                              const getUpdateAgeColor = (dateStr) => {
                                if (!dateStr) return 'text-gray-500';
                                try {
                                  const [day, month, year] = dateStr.split('/');
                                  const updateDate = new Date(year, month - 1, day);
                                  const now = new Date();
                                  const diffDays = Math.floor((now - updateDate) / (1000 * 60 * 60 * 24));
                                  
                                  if (diffDays < 7) return 'text-green-600 dark:text-green-400';
                                  if (diffDays < 14) return 'text-gray-600 dark:text-gray-400';
                                  return 'text-red-600 dark:text-red-400';
                                } catch {
                                  return 'text-gray-600 dark:text-gray-400';
                                }
                              };
                              
                              const ageColor = getUpdateAgeColor(project.lastUpdate.date);
                              
                              return (
                                <div className="mb-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => setExpandedUpdate(expandedUpdate === project.id ? null : project.id)}
                                      className="flex items-center gap-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 rounded -ml-2 flex-1 transition-colors"
                                    >
                                      <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className={`text-xs font-medium ${ageColor}`}>Last Update:</span>
                                          {project.lastUpdate.date && project.lastUpdate.time && (
                                            <span className={`text-xs ${ageColor}`}>
                                              {project.lastUpdate.date} at {project.lastUpdate.time}
                                            </span>
                                          )}
                                          {project.lastUpdate.author && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">by {project.lastUpdate.author}</span>
                                          )}
                                        </div>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{project.lastUpdate.text || 'No recent updates'}</p>
                                      </div>
                                      {expandedUpdate === project.id ? (
                                        <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                      ) : (
                                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                      )}
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openUpdateModal(project);
                                      }}
                                      className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex-shrink-0"
                                      title="Add Update"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Deadline - Inline Editable */}
                            <div className="flex items-center gap-2 mb-2 ml-2">
                              <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Deadline:</span>
                              {editingDeadline === project.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="date"
                                    value={tempDeadline}
                                    onChange={(e) => setTempDeadline(e.target.value)}
                                    className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  />
                                  <button
                                    onClick={() => saveDeadline(project.id)}
                                    className="p-1 bg-green-600 hover:bg-green-700 text-white rounded"
                                    title="Save"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEditingDeadline}
                                    className="p-1 bg-red-600 hover:bg-red-700 text-white rounded"
                                    title="Cancel"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEditingDeadline(project.id, project.deadline)}
                                  className={`text-sm hover:underline ${getDeadlineColor(project.deadline)}`}
                                >
                                  {formatDate(project.deadline)}
                                </button>
                              )}
                            </div>

                            {/* Calculated Value */}
                            <div className="flex items-center gap-2 ml-2 mb-3">
                              <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Value:</span>
                              <span className="text-sm font-bold text-green-600 dark:text-green-400">
                                {formatCurrency(calculateProjectValue(project))}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({project.probability}%)
                              </span>
                            </div>

                            {/* Action Buttons */}
                            <div className="ml-2 flex gap-2">
                              <button
                                onClick={() => openEditProjectModal(project)}
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-lg transition-colors"
                              >
                                <Edit className="w-3 h-3" />
                                Edit
                              </button>
                              <button
                                onClick={() => openUpdateModal(project)}
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                              >
                                <Edit className="w-3 h-3" />
                                Add Update
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Update History - Expandable */}
                      {expandedUpdate === project.id && (
                        <UpdateHistorySection 
                          project={project} 
                          onUpdateChange={(newLastUpdate) => {
                            // Update just this project's lastUpdate in state without full refresh
                            setApiProjects(prevProjects => 
                              prevProjects.map(p => 
                                p.id === project.id ? {...p, lastUpdate: newLastUpdate} : p
                              )
                            );
                          }}
                        />
                      )}
                    </div>
                        ))}
                      </div>
                      
                      {/* Category Summary */}
                      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {status} Category Total:
                          </span>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-xs text-gray-500 dark:text-gray-400">Calculated Value</div>
                              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                {formatCurrency(statusProjects.reduce((sum, p) => sum + calculateProjectValue(p), 0))}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500 dark:text-gray-400">Total Value</div>
                              <div className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatCurrency(statusProjects.reduce((sum, p) => sum + (p.value || 0), 0))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  });
                })()}

                {/* Summary Total */}
                {filteredProjects.length > 0 && (
                  <div className="mt-6 pt-4 border-t-2 border-gray-300 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Total Value of Displayed Projects:
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Calculated Value</div>
                          <div className="text-xl font-bold text-green-600 dark:text-green-400">
                            {formatCurrency(filteredProjects.reduce((sum, p) => sum + calculateProjectValue(p), 0))}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Value</div>
                          <div className="text-xl font-bold text-gray-900 dark:text-white">
                            {formatCurrency(filteredProjects.reduce((sum, p) => sum + p.value, 0))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {!selectedEmployee && (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Select an employee to view their detailed analysis</p>
          </div>
        )}
      </div>

      {/* Add Update Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Project Update</h3>
                <button
                  onClick={closeUpdateModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              {selectedProject && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-semibold">{selectedProject.field_2_raw}</span> - {selectedProject.field_5_raw}
                </div>
              )}
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Update Text
              </label>
              <textarea
                value={updateText}
                onChange={(e) => setUpdateText(e.target.value)}
                rows={6}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter project update details..."
              />
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeUpdateModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveUpdate}
                disabled={!updateText.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                Save Update
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Project Edit Modal */}
      {showEditProjectModal && editingProject && (
        <ProjectModal
          project={editingProject}
          onClose={closeEditProjectModal}
          onSave={handleProjectSaved}
        />
      )}

      {/* Status Visibility Filter Modal */}
      {showModal?.type === 'statusFilter' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Visible Status Filters
            </h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {statusOptions.map((status) => (
                <label key={status.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visibleStatuses.includes(status.field_84_raw || status.field_84)}
                    onChange={(e) => {
                      const statusName = status.field_84_raw || status.field_84;
                      if (e.target.checked) {
                        setVisibleStatuses([...visibleStatuses, statusName]);
                      } else {
                        setVisibleStatuses(visibleStatuses.filter(s => s !== statusName));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {status.field_84_raw || status.field_84}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Update History Section Component
const UpdateHistorySection = ({ project, onUpdateChange }) => {
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editingIndex, setEditingIndex] = React.useState(null);
  const [editText, setEditText] = React.useState('');

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/projects/${project.id}/updates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const updates = data.updates || [];
        setHistory(updates);
        
        // Update the lastUpdate in parent component (newest update is first)
        if (onUpdateChange && updates.length > 0) {
          onUpdateChange(updates[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching update history:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchHistory();
  }, [project.id]);

  const startEdit = (index, currentText) => {
    setEditingIndex(index);
    setEditText(currentText);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditText('');
  };

  const saveEdit = async (updateId) => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/projects/updates/${updateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          update_text: editText
        })
      });

      if (response.ok) {
        await fetchHistory();
        cancelEdit();
      } else {
        alert('Failed to update');
      }
    } catch (error) {
      console.error('Error saving update:', error);
      alert('Failed to save update');
    }
  };

  const deleteUpdate = async (updateId) => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/projects/updates/${updateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchHistory();
      } else {
        alert('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting update:', error);
      alert('Failed to delete update');
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 p-4">
      <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Update History</h5>
      {loading ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <p className="text-sm">Loading history...</p>
        </div>
      ) : history.length > 0 ? (
        <div className="space-y-3">
          {history.map((update, index) => (
            <div key={index} className="flex gap-3 pb-3 border-b border-gray-200 dark:border-gray-600 last:border-0 group">
              <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-600"></div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    {update.date && <span className="font-medium">{update.date}</span>}
                    {update.time && <span>at {update.time}</span>}
                    {update.author && (
                      <>
                        <span>•</span>
                        <span>{update.author}</span>
                      </>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {editingIndex === index ? (
                      <>
                        <button
                          onClick={() => saveEdit(update.id)}
                          className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                          title="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEdit(index, update.text)}
                          className="p-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteUpdate(update.id)}
                          className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editingIndex === index ? (
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full text-sm p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    autoFocus
                  />
                ) : (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{update.text}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <p className="text-sm">No update history yet.</p>
          <p className="text-xs mt-1">Updates will appear here after you add them.</p>
        </div>
      )}
    </div>
  );
};

export default AirportsAdminNew;
